import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { authFetchJson } from "@/lib/auth-fetch";

type PreviewResponse = {
  subject: string;
  html: string;
  text: string;
  securityCritical: boolean;
};

const EVENT_OPTIONS: ReadonlyArray<{ value: string; label: string; group: string }> = [
  { value: "booking.created", label: "New booking received", group: "Customer-facing" },
  { value: "payment.verified", label: "Payment verified", group: "Customer-facing" },
  { value: "payment.rejected", label: "Payment rejected", group: "Customer-facing" },
  { value: "payment.refunded", label: "Payment refunded", group: "Customer-facing" },
  { value: "ticket.created", label: "Support ticket received", group: "Customer-facing" },
  { value: "ticket.replied", label: "Support ticket reply", group: "Customer-facing" },
  { value: "ticket.assigned", label: "Ticket assigned (agent)", group: "Internal" },
  { value: "auth.invitation_sent", label: "Team invitation", group: "Internal" },
  { value: "auth.password_reset_requested", label: "Password reset", group: "Security" },
  { value: "auth.email_verify_requested", label: "Email verification", group: "Security" },
  { value: "auth.email_change_requested", label: "Email change confirmation", group: "Security" },
  { value: "auth.password_changed", label: "Password changed alert", group: "Security" },
  { value: "auth.mfa_enabled", label: "Two-factor enabled", group: "Security" },
  { value: "auth.mfa_disabled", label: "Two-factor disabled", group: "Security" },
  { value: "auth.recovery_code_used", label: "Recovery code used", group: "Security" },
  { value: "auth.account_deleted", label: "Account deleted", group: "Security" },
  { value: "auth.magic_link", label: "Magic sign-in link", group: "Customer-facing" },
];

/**
 * Workspace email preview pane. Picks an event from the dropdown,
 * calls `POST /api/v1/tenants/me/notification-preview`, and renders
 * the resulting HTML body inside a sandboxed iframe alongside the
 * plain-text alternative.
 *
 * No real email is sent; payloads are sample data.
 */
export function EmailPreviewer() {
  const [eventType, setEventType] = useState<string>("booking.created");
  const [data, setData] = useState<PreviewResponse | null>(null);

  const preview = useMutation({
    mutationFn: (event: string) =>
      authFetchJson<PreviewResponse>(`/api/v1/tenants/me/notification-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventType: event }),
      }),
    onSuccess: setData,
  });

  useEffect(() => {
    preview.mutate(eventType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  type EventOption = { value: string; label: string; group: string };
  const grouped = useMemo(() => {
    const map = new Map<string, EventOption[]>();
    for (const opt of EVENT_OPTIONS) {
      const list = map.get(opt.group) ?? [];
      list.push(opt);
      map.set(opt.group, list);
    }
    return map;
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium" htmlFor="preview-event">
          Preview event:
        </label>
        <div className="w-72">
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger id="preview-event">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...grouped.entries()].map(([group, opts]) => (
                <div key={group}>
                  <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {group}
                  </div>
                  {opts.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {preview.isPending && !data && (
        <p className="text-sm text-muted-foreground">Rendering preview…</p>
      )}

      {preview.isError && (
        <p className="text-sm text-destructive">
          {preview.error instanceof Error ? preview.error.message : "Couldn't render preview."}
        </p>
      )}

      {data && (
        <div className="space-y-4">
          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="px-4 py-2 border-b bg-muted/40 flex items-center justify-between">
              <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                HTML preview
              </p>
              <p className="text-xs text-muted-foreground truncate ml-4">
                <span className="font-medium">Subject:</span> {data.subject}
              </p>
            </div>
            <HtmlIframe html={data.html} />
          </div>

          <div className="rounded-lg border bg-card overflow-hidden">
            <div className="px-4 py-2 border-b bg-muted/40">
              <p className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                Plain-text alternative
              </p>
            </div>
            <pre className="px-4 py-3 text-xs whitespace-pre-wrap break-words font-mono leading-relaxed max-h-[360px] overflow-auto">
              {data.text}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function HtmlIframe({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement | null>(null);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(html);
    doc.close();
  }, [html]);

  return (
    <iframe
      ref={ref}
      title="Email preview"
      sandbox="allow-same-origin"
      className="w-full h-[640px] bg-white"
    />
  );
}
